import { useEffect, useState, useCallback, useRef } from "react";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import { FormField } from "@/components/ui/FormField";
import { createTrailOffer, updateTrailOffer } from "@/services/offers";

interface OfferModalProps {
    offer?: any;
    onClose: () => void;
    onSuccess: () => void;
}

interface OfferFormData {
    Image?: FileList;
    Days: string;
}

export default function TrailOfferModal({
    offer,
    onClose,
    onSuccess,
}: OfferModalProps) {
    const {
        control,
        register,
        handleSubmit,
        reset,
        setError,
        clearErrors,
        formState: { isSubmitting, errors },
    } = useForm<OfferFormData>({
        defaultValues: {
            Days: "",
        },
    });

    const [previewImage, setPreviewImage] = useState("");
    const [selectedFileName, setSelectedFileName] = useState("");
    const [removeImage, setRemoveImage] = useState(false);
    const [generalError, setGeneralError] = useState("");
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (!offer) {
            reset();
            setPreviewImage("");
            setSelectedFileName("");
            setRemoveImage(false);
            setGeneralError("");
            return;
        }

        reset({
            Days: Number(offer.Days || 0).toString(),
        });

        setRemoveImage(false);
        setGeneralError("");

        if (offer.Image) {
            const imageBaseUrl = import.meta.env.VITE_BASE_URL.replace("/api", "");
            setSelectedFileName(offer.Image.split("/").pop() || "");
            setPreviewImage(`${imageBaseUrl}${offer.Image}`);
        } else {
            setPreviewImage("");
            setSelectedFileName("");
        }
    }, [offer, reset]);

    const handleApiErrors = useCallback(
        (response: any) => {
            clearErrors();
            setGeneralError("");

            // Axios errors: error.response.data
            // Direct response returns: response.data
            const data =
                response?.data ||   // axios thrown error
                response?.errors ||              // direct axios response
                response ||                    // plain object fallback
                {};
            if (!data || typeof data !== "object") {
                toast.error("Something went wrong");
                return;
            }
            let hasFieldError = false;

            Object.entries(data).forEach(([key, value]: any) => {
  const message = Array.isArray(value) ? value[0] : value;

  switch (key) {
    case "Days":
      setError("Days", {
        type: "server",
        message,
      });
      hasFieldError = true;
      break;

    case "Status":
      setGeneralError(message);
      toast.error(message);
      hasFieldError = true;
      break;
  }
});

            if (data.error) {
                const msg = Array.isArray(data.error) ? data.error[0] : data.error;
                setGeneralError(msg);
                toast.error(msg);
            } else if (!hasFieldError) {
                toast.error("Something went wrong");
            }
        },
        [clearErrors, setError]
    );

    const handleRemoveImage = () => {
        setPreviewImage("");
        setSelectedFileName("");
        setRemoveImage(true);
        // Clear the file input so the same file can be re-selected if needed
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const onSubmit = async (values: OfferFormData) => {
        try {
            clearErrors();
            setGeneralError("");

            // Inline validation — shows error under field instead of only toast
            let hasError = false;

            if (!values.Days) {
                setError("Days", {
                    type: "manual",
                    message: "Days is required",
                });
                hasError = true;
            }

            if (hasError) return;

            const formData = new FormData();

            formData.append("Days", values.Days);
            formData.append("Status", "true");

            if (values.Image?.[0]) {
                // New image selected
                formData.append("Image", values.Image[0]);
            } else if (removeImage) {
                // User explicitly removed existing image — send empty string to clear it
                formData.append("Image", "");
            }

            const response = offer
                ? await updateTrailOffer(offer.id, formData)
                : await createTrailOffer(formData);

            if (response?.status === 200 || response?.status === 201) {
                toast.success(
                    offer ? "Trail Offer updated successfully" : "Trail Offer created successfully"
                );
                onSuccess();
            } else {
                handleApiErrors(response);
            }
        } catch (error) {
            handleApiErrors(error);
        }
    };

    // Merge register ref with our local ref so we can reset the input
    const { ref: registerRef, ...imageRegisterProps } = register("Image", {
        onChange: (e) => {
            const file = e.target.files?.[0];
            if (file) {
                setSelectedFileName(file.name);
                setPreviewImage(URL.createObjectURL(file));
                setRemoveImage(false);
            }
        },
    });

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex justify-between items-center p-5 bg-gray-800 rounded-t-2xl flex-shrink-0">
                    <h2 className="text-white text-xl font-bold">
                        {offer ? "Edit Trail Offer" : "Create Trail Offer"}
                    </h2>
                    <button onClick={onClose} type="button" aria-label="Close modal">
                        <X className="text-white" />
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="overflow-y-auto flex-1">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="p-6 grid gap-5">

                            {/* Discount Percentage */}
                            <FormField
                                control={control}
                                name="Days"
                                label="Days"
                                type="number"
                                required
                                error={errors.Days?.message}
                            />

                            {/* Image upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Offer Image
                                </label>

                                <input
                                    type="file"
                                    accept="image/*"
                                    {...imageRegisterProps}
                                    ref={(el) => {
                                        registerRef(el);
                                        fileInputRef.current = el;
                                    }}
                                    className="block w-full text-sm text-gray-500
                    file:mr-3 file:py-2 file:px-4
                    file:rounded file:border file:border-gray-300
                    file:text-sm file:font-medium
                    file:bg-white file:text-gray-700
                    hover:file:bg-gray-50"
                                />

                                {/* Image preview with remove button */}
                                {previewImage && (
                                    <div className="relative inline-block mt-3">
                                        <img
                                            src={previewImage}
                                            alt="Trail Offer preview"
                                            className="w-40 rounded-xl object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleRemoveImage}
                                            aria-label="Remove image"
                                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md transition-colors"
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* General / server error */}
                            {generalError && (
                                <p className="text-red-500 text-sm">{generalError}</p>
                            )}
                        </div>

                        {/* Footer buttons — always visible */}
                        <div className="p-5 flex gap-3 border-t border-gray-100 flex-shrink-0">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 py-3 rounded transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="flex-1 bg-yellow-400 hover:bg-yellow-500 py-3 rounded font-medium transition-colors disabled:opacity-60"
                            >
                                {isSubmitting ? "Saving..." : offer ? "Update" : "Create"}
                            </button>
                        </div>
                    </form>
                </div>

            </div>
        </div>
    );
}